import ErrorHandler from '@/services/ErrorHandler'
import { i18n } from '@/plugins/i18n'
import escape from 'lodash/escape'
import Store from '@/store/store'
import configType from '@/store/type/configType'

class Notification {
    setVueInstance(vue) {
        this.vue = vue
    }

    notification(title = '', msg = '', type = 'success', timeout = 5000) {
        this.vue.$toastr.Add({
            title,
            msg,
            type,
            timeout,
            preventDuplicates: true,
        })
    }

    error(title, msg, timeout) {
        this.notification(title, msg, 'error', timeout)
    }

    warning(title, msg, timeout) {
        this.notification(title, msg, 'warning', timeout)
    }

    success(title, msg, timeout) {
        this.notification(title, msg, 'success', timeout)
    }

    info(title, msg, timeout) {
        this.notification(title, msg, 'info', timeout)
    }

    // general notifications

    batchDeleteFinished(result) {
        this.success(
            i18n.t('notifications.batch_delete_finished.title'),
            i18n.t('notifications.batch_delete_finished.message', {
                count: result.deleted.length,
            })
        )
    }

    batchDeleteFinishedWithErrors(result) {
        this.warning(
            i18n.t('notifications.batch_delete_finished_with_error.title'),
            i18n.t('notifications.batch_delete_finished_with_error.message', {
                count: result.deleted.length,
                list: result.failed.join(', '),
            })
        )
    }

    batchDeleteCanceled(result) {
        const suffix =
            result.deleted.length > 0 ? 'has_deleted_items' : 'no_deleted_items'

        this.warning(
            i18n.t('notifications.batch_delete_cancel.title'),
            i18n.t('notifications.batch_delete_cancel.' + suffix, {
                list: result.deleted.join(', '),
            }),
            0
        )
    }

    entityCreated(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.entity_created.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    entityUpdated(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.entity_updated.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    entityDeleted(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.entity_deleted.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    entityActivated(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.entity_activated.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    entityDeactivated(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.entity_deactivated.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    restaurantAdminEnabled(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.restaurant_admin_enabled.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    restaurantAdminDisabled(entity, entityName) {
        this.success(
            i18n.t(entity || 'notifications.success'),
            i18n.t('notifications.restaurant_admin_disabled.message', {
                name: entityName || i18n.t('labels.entity'),
            })
        )
    }

    requestError(response) {
        ErrorHandler.error(response)
        response = response.message || response
        const networkError =
            response.toString().toLowerCase() === 'network error'

        if (networkError) this.networkError()
        else
            this.error(
                i18n.t('notifications.request_error.title'),
                response,
                10000
            )
    }

    networkError() {
        const locale = Store.getters[configType.getters.CURRENT_LOCALE]
        const title = locale === 'hu' ? 'Hiba' : 'Error'
        const response =
            locale === 'hu'
                ? 'Az API szerver nem válaszol'
                : 'API server not responding'
        this.error(title, response, 5000)
    }

    formError() {
        this.error(i18n.t('notifications.form_error.title'), '')
    }

    // A 409 carries the server's reason (e.g. a company team used by an active canteen, tasks#176); every other
    // failure keeps the generic message, because a 500's description can hold raw SQL / exception text. The toast
    // renders title and message as HTML, so both are escaped (entity and canteen names are user data).
    deleteError(entityName, reason) {
        const title = escape(
            i18n.t('notifications.delete_error.title', { name: entityName })
        )
        const description = reason?.response?.data?.['hydra:description']
        if (reason?.response?.status === 409 && description) {
            this.error(title, escape(description), 10000)
            return
        }
        this.error(title, '')
    }

    importSuccessful(name) {
        this.success(
            i18n.t(name),
            i18n.t('notifications.import_successful.title')
        )
    }
}

export default new Notification()
